import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supportedLanguages, defaultLanguage, type SupportedLanguage } from '@/i18n'
import { PublicShell, ShellPlaceholder, NotFound } from '@/components/layout/SiteShell'
import { RouteLifecycle } from '@/components/layout/RouteLifecycle'
import { getLocalizedPath, localizedRoutes } from '@/i18n/routes'

function lazyNamed<T extends Record<K, ComponentType<any>>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K,
) {
  return lazy(async () => ({ default: (await loader())[name] }))
}

const HomePage = lazyNamed(() => import('@/pages/HomePage'), 'HomePage')
const SolutionsPage = lazyNamed(() => import('@/pages/SolutionsPage'), 'SolutionsPage')
const CategoryPage = lazyNamed(() => import('@/pages/CategoryPage'), 'CategoryPage')
const ProductPage = lazyNamed(() => import('@/pages/ProductPage'), 'ProductPage')
const NewsPage = lazyNamed(() => import('@/pages/NewsPage'), 'NewsPage')
const InstitutionalPage = lazyNamed(() => import('@/pages/InstitutionalPage'), 'InstitutionalPage')
const ContactPage = lazyNamed(() => import('@/pages/ContactPage'), 'ContactPage')
const AdminDemoPage = lazyNamed(() => import('@/pages/admin-demo/AdminDemoPage'), 'AdminDemoPage')

function Deferred({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}><RouteReady>{children}</RouteReady></Suspense>
}

function RouteReady({ children }: { children: ReactNode }) {
  useEffect(() => { window.dispatchEvent(new Event('esamed:route-ready')) }, [])
  return children
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation('common')
  const valid = supportedLanguages.includes(lang as SupportedLanguage)
  useEffect(() => {
    const current = valid && lang ? lang : defaultLanguage
    document.documentElement.lang = current
    if (i18n.resolvedLanguage !== current) void i18n.changeLanguage(current)
  }, [lang, i18n, valid])
  if (!valid) return <PublicShell><NotFound /></PublicShell>
  return <><RouteLifecycle /><Outlet /></>
}

function LegacyPrivacyRedirect() {
  const { lang } = useParams<{ lang: string }>()
  const language = supportedLanguages.includes(lang as SupportedLanguage) ? lang as SupportedLanguage : defaultLanguage
  return <Navigate to={getLocalizedPath('privacy', language)} replace />
}

export function LanguageRouter() {
  return <Routes>
    <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />
    <Route path="/:lang" element={<LanguageWrapper />}>
      <Route index element={<PublicShell><Deferred><HomePage /></Deferred></PublicShell>} />
      {supportedLanguages.map((language) => <Route key={`solutions-${language}`} path={localizedRoutes.solutions[language]} element={<PublicShell><Deferred><SolutionsPage /></Deferred></PublicShell>} />)}
      <Route path="categoria/:categorySlug" element={<PublicShell><Deferred><CategoryPage /></Deferred></PublicShell>} />
      <Route path="category/:categorySlug" element={<PublicShell><Deferred><CategoryPage /></Deferred></PublicShell>} />
      <Route path="prodotti/:categorySlug/:productSlug" element={<PublicShell><Deferred><ProductPage /></Deferred></PublicShell>} />
      <Route path="products/:categorySlug/:productSlug" element={<PublicShell><Deferred><ProductPage /></Deferred></PublicShell>} />
      {supportedLanguages.map((language) => <Route key={`news-${language}`} path={localizedRoutes.news[language]} element={<PublicShell><Deferred><NewsPage /></Deferred></PublicShell>} />)}
      {supportedLanguages.map((language) => <Route key={`contacts-${language}`} path={localizedRoutes.contacts[language]} element={<PublicShell><Deferred><ContactPage /></Deferred></PublicShell>} />)}
      {supportedLanguages.map((language) => <Route key={`certifications-${language}`} path={localizedRoutes.certifications[language]} element={<PublicShell><Deferred><InstitutionalPage kind="certifications" /></Deferred></PublicShell>} />)}
      {supportedLanguages.map((language) => <Route key={`careers-${language}`} path={localizedRoutes.careers[language]} element={<PublicShell><Deferred><InstitutionalPage kind="careers" /></Deferred></PublicShell>} />)}
      {supportedLanguages.map((language) => <Route key={`privacy-${language}`} path={localizedRoutes.privacy[language]} element={<PublicShell><Deferred><InstitutionalPage kind="privacy" /></Deferred></PublicShell>} />)}
      {supportedLanguages.map((language) => <Route key={`cookies-${language}`} path={localizedRoutes.cookies[language]} element={<Navigate to={getLocalizedPath('privacy', language)} replace />} />)}
      <Route path="privacy" element={<LegacyPrivacyRedirect />} />
      {supportedLanguages.map((language) => <Route key={language} path={`${localizedRoutes.reserved[language]}/*`} element={<Deferred><AdminDemoPage /></Deferred>} />)}
      <Route path="*" element={<PublicShell><ShellPlaceholder /></PublicShell>} />
    </Route>
  </Routes>
}
